import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    formatBpsPercent,
    RATE_FIELDS,
    regimeLabelKey,
} from '../../settingsMeta';
import type { RateHistoryRow, UseRateHistoryResult } from './useRateHistory';

type RateHistoryViewProps = {
    history: UseRateHistoryResult;
};

/**
 * One recorded change: who, when, what and why.
 *
 * The note gets its own line at full width rather than a truncated cell,
 * because it is the only field that explains *why* a rate moved — which is the
 * first thing an auditor asks and the last thing that survives a table squeeze.
 */
const HistoryRow: React.FC<{ row: RateHistoryRow }> = ({ row }) => {
    const { t, i18n } = useTranslation('pages');
    const { set } = row;

    return (
        <li
            className="flex flex-col gap-3 rounded-xl bg-surface-secondary p-4"
            {...getNodeTestIdProp(`settings-history-row-${set.id}`)}
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold tabular-nums text-foreground">
                        {set.effectiveFrom}
                    </span>
                    <span className="text-xs text-muted">
                        {t(regimeLabelKey(set.regime))}
                    </span>
                    {row.isInForce ? (
                        <Chip color="success" size="sm" variant="soft">
                            {t('settings.history.inForce')}
                        </Chip>
                    ) : null}
                    {row.isScheduled ? (
                        <Chip color="warning" size="sm" variant="soft">
                            {t('settings.history.scheduled')}
                        </Chip>
                    ) : null}
                </span>
                <span className="text-xs text-muted">
                    {/* A seeded set has no author — say so rather than
                        rendering an empty column. */}
                    {t('settings.history.recordedBy', {
                        who:
                            set.createdByUserId ??
                            t('settings.history.systemAuthor'),
                        when: new Date(set.createdAt).toLocaleString(
                            i18n.language,
                        ),
                    })}
                </span>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                {RATE_FIELDS.map((field) => (
                    <div className="flex flex-col" key={field.key}>
                        <dt className="text-xs text-muted">
                            {t(field.labelKey)}
                        </dt>
                        <dd className="text-sm tabular-nums text-foreground">
                            {formatBpsPercent(
                                set.rates[field.key],
                                i18n.language,
                            )}
                        </dd>
                    </div>
                ))}
            </dl>

            {set.note ? (
                <p
                    className="text-sm text-muted"
                    {...getNodeTestIdProp(`settings-history-note-${set.id}`)}
                >
                    {set.note}
                </p>
            ) : null}
        </li>
    );
};

export const RateHistoryView: React.FC<RateHistoryViewProps> = ({
    history,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('settings.history.heading')}
            className="flex w-full flex-col gap-4"
            {...getNodeTestIdProp('settings-history')}
        >
            <div className="flex flex-col gap-1">
                <h2 className="text-sm font-semibold text-foreground">
                    {t('settings.history.heading')}
                </h2>
                <p className="text-xs text-muted">
                    {t('settings.history.intro')}
                </p>
            </div>

            {history.isEmpty ? (
                <p
                    className="text-sm text-muted"
                    {...getNodeTestIdProp('settings-history-empty')}
                >
                    {t('settings.history.empty')}
                </p>
            ) : (
                <ul className="flex flex-col gap-3">
                    {history.rows.map((row) => (
                        <HistoryRow key={row.set.id} row={row} />
                    ))}
                </ul>
            )}
        </section>
    );
};
